import Spinner from "@/components/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useQuizById } from "@/hooks/quiz-by-id.hook";
import { useSubmitQuizMutation } from "@/hooks/submit-quiz-mutation.hook";
import { FC, useEffect, useState } from "react";

type SendQuizDialogProps = {
  showDialog: boolean;
  showDialogChange: (value: boolean) => void;
  quizId: string;
};

const SendQuizDialog: FC<SendQuizDialogProps> = ({
  showDialog,
  showDialogChange,
  quizId,
}) => {
  const [isPendingResponsesDialogOpen, setIsPendingResponsesDialogOpen] =
    useState(false);

  const [isConfirmResponsesDialogOpen, setIsConfirmResponsesDialogOpen] =
    useState(false);

  const { data: userProfile } = useCurrentUserProfile();

  const { data: quiz } = useQuizById({
    companyId: userProfile?.companyId || "",
    applicationQuizId: quizId,
  });

  const mutation = useSubmitQuizMutation();

  const handleSubmitQuiz = () => {
    mutation.mutate({
      companyId: userProfile?.companyId || "",
      quizId,
    });
  };

  // If a dialog must be shown, use the quiz to decide
  // which message should be displayed
  useEffect(() => {
    if (showDialog) {
      if (quiz?.summary.respondedQuestionsPercentage === 100) {
        setIsConfirmResponsesDialogOpen(true);
      } else {
        setIsPendingResponsesDialogOpen(true);
      }
    }
  }, [showDialog]);

  // Fire the component callback when any of
  // these dialogs are closed
  useEffect(() => {
    if (!isPendingResponsesDialogOpen && !isConfirmResponsesDialogOpen) {
      showDialogChange(false);
    }
  }, [isPendingResponsesDialogOpen, isConfirmResponsesDialogOpen]);

  useEffect(() => {
    if (mutation.isSuccess) {
      setIsConfirmResponsesDialogOpen(false);
    }
  }, [mutation.isSuccess]);

  return (
    <>
      <AlertDialog
        open={isPendingResponsesDialogOpen}
        onOpenChange={setIsPendingResponsesDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Aún no has completado el cuestionario
            </AlertDialogTitle>
            <AlertDialogDescription>
              Aún te faltan preguntas por responder para poder enviar tu
              cuestionario. Por favor, completa todas las preguntas antes de
              poder enviarlo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Seguir respondiendo</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={isConfirmResponsesDialogOpen}
        onOpenChange={setIsConfirmResponsesDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Estás seguro de enviar tu cuestionario?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Una vez enviado, ya no podrás editar tus respuestas
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>
              Continuar respondiendo
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleSubmitQuiz();
              }}
              disabled={mutation.isPending}
            >
              {mutation.isPending && <Spinner className="mr-1" />}
              Enviar cuestionario
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SendQuizDialog;
